import { memo } from 'react';
import { TrafficLightMarker } from './TrafficLightMarker';
import type { GroupDefinition, ColorCode } from '../../types/intersection';
import { osmGroupByCabinetIdResolved } from '../../data/osm-group-mapping-resolved';
import { signalPositionById } from '../../data/santa-coloma';

interface IntersectionMarkersProps {
  cabinetId: number;
  groups: GroupDefinition[];
  groupColors: ColorCode[];
}

// Offsets for groups sharing the same pole (~3m displacement to avoid overlap)
const GROUP_OFFSETS: Record<number, [number, number]> = {
  5:  [0.00003, 0.00003],    // G5 shares pole with G13 (NE)
  13: [-0.00003, -0.00003],  // G13 shares pole with G5 (NE)
  8:  [0.00003, 0.00003],    // G8 near G11 (NE corner)
  11: [-0.00003, -0.00003],  // G11 near G8 (NE corner)
};

// memo: `groups` es estable y `groupColors` mantiene su referencia entre ticks
// (useMemo por currentPhase en useIntersectionState). Así los grupos solo
// re-renderizan al cambiar de fase, no en cada tick de la simulación.
export const IntersectionMarkers = memo(function IntersectionMarkers({
  cabinetId,
  groups,
  groupColors,
}: IntersectionMarkersProps) {
  const wiring = osmGroupByCabinetIdResolved[cabinetId];

  return (
    <>
      {groups.map((group, index) => {
        // Un grupo puede tener más de un semáforo OSM cableado a la vez
        // (dos postes del mismo cruce) — el panel de edición lo permite, así
        // que hay que pintar un marcador convergido por cada asignación, no
        // solo la primera.
        const assignments = wiring?.filter(w => w.groupId === group.id) ?? [];
        // Sin cableado confirmado no hay nada real que mostrar — la posición
        // sintética (offset cardinal ciego desde el cabinet) no tiene
        // relación con la calle real y llega a caer dentro de manzanas
        // (reportado por ARS93 2026-09-18). Mejor un mapa con menos
        // marcadores pero honesto que uno con 1200+ posiciones inventadas.
        if (assignments.length === 0) return null;
        return assignments.map(assignment => (
          <TrafficLightMarker
            key={`${group.id}-${assignment.osmId}`}
            group={group}
            colorCode={groupColors[index]}
            offset={GROUP_OFFSETS[group.id]}
            realPosition={signalPositionById[assignment.osmId]}
          />
        ));
      })}
    </>
  );
});
